import React, { useEffect, useRef, useState } from "react";
import { Form, Input, DatePicker, Button, Row, Col, Popconfirm, Upload, Flex, Card, Tabs, message, Typography, Drawer, Select, Tooltip } from 'antd';
import Table from 'components/Common/Table';
import { EditOutlined, DeleteOutlined, InboxOutlined, EyeOutlined, DownloadOutlined, SwapOutlined, LinkOutlined, SwapRightOutlined } from "@ant-design/icons"
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams } from "react-router-dom";
import fileDownload from "js-file-download";
import { useNotification } from 'NotificationContext';
import PdfPreviewModal from 'components/PdfPreviewModal/PdfPreviewModal';
import ActivityThread from "components/Activities/ActivityThread";
import LeadActivityForm from "./LeadActivityForm";
import { LEAD_STATUS_OPTIONS } from "components/Leads/leadStatus";
import { LeadTagSelect } from "components/Leads/leadTags";
import LeadTaskForm from "./LeadTaskForm";
import LeadRequirements, { requirementToFormValues, formValuesToRequirement } from "components/Leads/LeadRequirements";
import ExtraFieldsList, { extraDataToRows, rowsToExtraData } from "components/ExtraFields/ExtraFieldsList";

import dayjs from 'dayjs';
import axios from 'axios';

const { Dragger } = Upload;

const LeadForm = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [attachments, setAttachments] = useState([]);
    // The loaded lead record itself - the send drawer needs products, email
    // and wa_number, which the form values alone don't carry.
    const [lead, setLead] = useState(null);
    const [leadActivities, setLeadActivities] = useState([]);

    const [tasks, setTasks] = useState([]);
    const [, setLoading] = useState(false);
    const [paymentProducts, setPaymentProducts] = useState([]);
    const [leadTags, setLeadTags] = useState([]);
    // Keys already stored on the lead. These render as fixed labels and cannot
    // be removed; rows added in this session stay editable until saved.
    const [savedExtraKeys, setSavedExtraKeys] = useState([]);

    const pollTimer = useRef(null);

    const [activityForm] = Form.useForm();
    const [openActivityForm, setOpenActivityForm] = useState(false);
    const [currentActivityInstance, setCurrentActivityInstance] = useState(null);
    // Watched here so the drawer can widen when the activity turns into a send.
    const activityType = Form.useWatch('activity_type', activityForm);
    const isSendingActivity = !currentActivityInstance
        && ['EMAIL', 'WHATSAPP'].includes(activityType);
    const [taskForm] = Form.useForm();
    const [openTaskForm, setOpenTaskForm] = useState(false);
    const [currentTaskInstance, setCurrentTaskInstance] = useState(null);

    const [previewPdfVisible, setPreviewPdfVisible] = useState(false);
    const [pdfPreviewData, setPdfPreviewData] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const notify = useNotification();

    const events = {
        onDocumentLoadSuccess: ({ numPages }) => {
            setNumPages(numPages);
        },
    }

    const saveLead = async (values) => {
        console.log('Received values:', values);
        setLoading(true);
        // `extra_fields` is a form-only array; fold it back into the
        // extra_data object the API expects.
        // `requirement_fields` likewise becomes the `requirement_data` payload.
        const { extra_fields, requirement_fields, ...rest } = values;
        const payload = {
            ...rest,
            extra_data: rowsToExtraData(extra_fields),
            requirement_data: formValuesToRequirement(requirement_fields),
        };
        if (id) {
            const response = await actions.updateLeads(payload, id);
            if (response?.status === 200) {
                notify("success", "Lead updated successfully");
                // Re-seed the extra rows from what the server stored, so a field
                // just added becomes a locked label like the imported ones.
                // Both must be set together - saved rows are matched by index.
                const rows = extraDataToRows(response.data?.extra_data);
                setSavedExtraKeys(rows.map((row) => row.key));
                form.setFieldsValue({ extra_fields: rows });
                // Keep the submitted answers shown under the card current.
                setLead((prev) => ({ ...prev, requirement: response.data?.requirement, status: response.data?.status }));
            } else {
                notify("error", "Failed to update lead");
            }
        } else {
            const response = await actions.addLeads(payload);
            if (response?.status === 201) {
                notify("success", "Lead created successfully");
                navigate('/leads/edit/' + response.data['id']);
            } else {
                notify("error", "Failed to create lead");
            }
        }
        setLoading(false);
    };

    const handleActivityDelete = async (id) => {
        const response = await actions.deleteLeadActivity(id);
        if (response) {
            const restAct = leadActivities.filter((act) => act.id !== id)
            setLeadActivities(restAct)
        }
    }

    const handleTaskDelete = async (id) => {
        const response = await actions.deleteLeadTask(id);
        if (response) {
            const restTasks = tasks.filter((task) => task.id !== id)
            setTasks(restTasks)
        }
    }


    const loadLead = async () => {
        if (id) {
            const response = await actions.retrieveLead(id);
            if (response) {
                const thisInstance = { ...response }
                let date_fields = ['converted_at', 'last_contacted_at', 'follow_up_date']
                for (let field of date_fields) {
                    thisInstance[field] = thisInstance[field] ? dayjs(thisInstance[field]) : null;
                }
                thisInstance.extra_fields = extraDataToRows(response?.extra_data);
                thisInstance.requirement_fields = requirementToFormValues(response?.requirement);
                setSavedExtraKeys(thisInstance.extra_fields.map((row) => row.key));
                form.setFieldsValue(thisInstance);
                setLead(response);
                setAttachments(response?.attachments);
                setLeadActivities(response?.lead_activities);
                setTasks(response?.tasks);
            }
            setLoading(false);
            return response;
        }
        setLoading(false);
        return null;
    }

    // Refresh just the activity timeline. Deliberately does NOT touch the form
    // fields the way loadLead does - polling must never overwrite edits the
    // user is in the middle of typing.
    const refreshActivities = async () => {
        if (!id) return null;
        const response = await actions.retrieveLead(id);
        if (response) {
            setLead(response);
            setLeadActivities(response?.lead_activities);
        }
        return response;
    }

    // Delivery is asynchronous: a message is written as QUEUED and Celery flips
    // it to SENT/FAILED a moment later. Poll briefly after a send so the row
    // settles on its own instead of looking stuck until a manual reload.
    const POLL_EVERY_MS = 2000;
    const POLL_MAX_TICKS = 8;

    const stopPolling = () => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
    };

    const hasQueuedMessage = (activities) =>
        (activities || []).some((activity) =>
            (activity?.messages || []).some((message) => message?.status === 'QUEUED')
        );

    const pollUntilDelivered = () => {
        stopPolling();
        let ticks = 0;
        pollTimer.current = setInterval(async () => {
            ticks += 1;
            const response = await refreshActivities();
            if (ticks >= POLL_MAX_TICKS || !hasQueuedMessage(response?.lead_activities)) {
                stopPolling();
            }
        }, POLL_EVERY_MS);
    };

    const loadPaymentProducts = async () => {
        const response = await actions.loadProductPaymentProducts();
        if (response && response.data) {
            setPaymentProducts(Array.isArray(response.data) ? response.data : response.data.results || []);
        }
    }

    const loadLeadTags = async () => {
        try {
            setLeadTags(await actions.listLeadTags());
        } catch (e) {
            console.log("Error", e);
        }
    }

    // Copy a link to the website's requirements form, tied to this lead: what
    // the client submits there lands on this lead instead of creating a new one.
    const [copyingLink, setCopyingLink] = useState(false);
    const copyRequirementLink = async () => {
        setCopyingLink(true);
        try {
            const { data } = await axios.get(`/lead/leads/${id}/requirement_link/`);
            try {
                await navigator.clipboard.writeText(data.url);
            } catch {
                // navigator.clipboard needs a secure context; plain-http hosts fall back.
                const el = document.createElement("textarea");
                el.value = data.url;
                document.body.appendChild(el);
                el.select();
                document.execCommand("copy");
                el.remove();
            }
            notify("success", "Requirements form link copied");
        } catch (err) {
            console.log("Error: ", err);
            notify("error", "Could not create the requirements form link");
        } finally {
            setCopyingLink(false);
        }
    };

    // Most leads message on the number they called from, so copying it over
    // beats retyping it - and a mistyped WhatsApp number fails silently at
    // send time.
    const useSameAsPhone = () => {
        const phone = form.getFieldValue('phone_number');
        if (!phone) {
            notify("warning", "No phone number yet", "Fill the phone number first.");
            return;
        }
        form.setFieldsValue({ wa_number: phone });
        notify("success", "WhatsApp number filled", "Save the lead to keep it.");
    };

    const handleConvertToDeal = () => {
        // Navigate to the convert page
        navigate(`/leads/convert/${id}`);
    };

    useEffect(() => {
        loadPaymentProducts();
        loadLeadTags();
    }, []);

    useEffect(() => {
        if (id) {
            loadLead()
        }
    }, [id]);

    // Never leave a poll running after the page goes away.
    useEffect(() => stopPolling, []);

    const props = {
        name: 'file',
        multiple: false,
        action: process.env.REACT_APP_BASE_URL + `/lead/leads/${id}/addAttachment/`,
        headers: { "Authorization": axios.defaults.headers.common['Authorization'] },
        onChange(info) {
            console.log(info);
            const { status, response } = info.file;
            if (status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (status === 'done') {
                message.success(`${info.file.name} file uploaded successfully.`);
            } else if (status === 'error') {
                message.error(`${info.file.name} file upload failed.`);
            }

            if (response) {
                setAttachments(response);
            }
        },
        onDrop(e) {
            console.log('Dropped files', e.dataTransfer.files);
        },
    };

    const viewDownloadDocument = async (record, preview, download) => {
        try {
            const response = await axios.get(record.document['document_file'], {
                responseType: "blob",
            });
            // console.log({ response, record });
            if (response.status === 200) {
                if (download) {
                    fileDownload(response.data, record.document['name']);
                }
                if (preview) {
                    if (record.document['extension'] === ".pdf") {
                        setPdfPreviewData(response.data);
                        setPreviewPdfVisible(true);
                    } else {
                        notify("error", "only pdfs can be rendered for now");
                    }
                }
            }
        } catch (err) {
            console.log("Error: ", err);
        }
    }

    const handleDeleteAttachment = async (doc_id) => {
        const response = await actions.removeDocumentFromLeads(id, doc_id);
        if (response) {
            setAttachments(response);
        }
    }

    const attachmentColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a?.id.localeCompare(b?.id),
        },
        {
            title: 'Name',
            dataIndex: ["document", "name"],
            key: 'name',
            sorter: (a, b) => a?.name.localeCompare(b?.name),
        },
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a, b) => dayjs(a?.created_at) - dayjs(b?.created_at),
            render: (text, record) => {
                return <div>{dayjs(text).format('MMMM Do YYYY, h:mm:ss a')}</div>
            }
        },
        {
            title: 'Size',
            dataIndex: ["document", "size"],
            key: 'size',
            sorter: (a, b) => a?.org_size - b?.org_size,
        },
        {
            title: 'Linked',
            dataIndex: ["document", "is_linked"],
            key: 'is_linked',
            sorter: (a, b) => a?.is_linked - b?.is_linked,
            render: (_, record) => {
                const size = 8;
                const radius = "30%";
                return (
                    <div className='d-flex justify-content-center align-items-center'>
                        {record.is_linked ? <>
                            <div style={{ width: size, height: size, backgroundColor: "green", borderRadius: radius }}>
                            </div>
                        </> : <>
                            <div style={{ width: size, height: size, backgroundColor: "red", borderRadius: radius }}>
                            </div>
                        </>}
                    </div>
                )
            }
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <Typography.Link onClick={() => { console.log("View Document"); viewDownloadDocument(record, true, false); }}>
                            <EyeOutlined />
                        </Typography.Link>
                        <Typography.Link onClick={() => { console.log("Download Document"); viewDownloadDocument(record, false, true); }}>
                            <DownloadOutlined />
                        </Typography.Link>
                        <Popconfirm title="Sure to delete?" onConfirm={() => handleDeleteAttachment(record.id)}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const taskColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a?.id.localeCompare(b?.id),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => a?.title.localeCompare(b?.title),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            sorter: (a, b) => a?.description - b?.description,
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            sorter: (a, b) => dayjs(a?.due_date) - dayjs(b?.due_date),
            render: (text, record) => {
                return <div>{dayjs(text).format('MMMM Do YYYY, h:mm:ss a')}</div>
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => a?.status - b?.status,
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <a>
                            <EditOutlined
                                style={{ verticalAlign: 'middle' }}
                                onClick={() => {
                                    setCurrentTaskInstance(record)
                                    setOpenTaskForm(true)
                                }}
                            />
                        </a>
                        <Popconfirm title="Sure to delete?"
                            onConfirm={() => handleTaskDelete(record.id)}
                        >
                            <a>
                                <DeleteOutlined />
                            </a>
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const tabElements = [
        {
            label: `Lead Activities`,
            key: '1',
            children:
                <ActivityThread
                    activities={leadActivities}
                    record={id ? { lead: id } : undefined}
                    onAdd={() => setOpenActivityForm(true)}
                    onEdit={(activity) => {
                        setCurrentActivityInstance(activity)
                        setOpenActivityForm(true)
                    }}
                    onDelete={handleActivityDelete}
                />,
        },
        {
            label: `Attachments`,
            key: '2',
            children:
                <div>
                    <Dragger {...props}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        <p className="ant-upload-hint">
                            Support for a single or bulk upload. Strictly prohibit from uploading company data or other
                            band files
                        </p>
                    </Dragger>
                    <Table
                        dataSource={attachments}
                        columns={attachmentColumns}
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
        {
            label: `Tasks`,
            key: '3',
            children:
                <div>
                    <Flex justify="end">
                        <Button
                            onClick={setOpenTaskForm}
                            type="primary"
                            style={{ marginBottom: 16 }}
                        >
                            Add Item
                        </Button>
                    </Flex>
                    <Table
                        dataSource={tasks}
                        columns={taskColumns}
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
    ]

    return (
        <>
            <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 10, paddingBottom: 30 }}>
                <Form form={form} onFinish={saveLead} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} labelAlign="left">
                    <Flex justify='end' gap='middle' style={{ margin: "10px auto" }}>
                        {id && (
                            <Button
                                type="default"
                                icon={<LinkOutlined />}
                                loading={copyingLink}
                                onClick={copyRequirementLink}
                            >
                                Copy Requirements Link
                            </Button>
                        )}
                        {id && (
                            <Button
                                type="default"
                                icon={<SwapOutlined />}
                                onClick={handleConvertToDeal}
                            >
                                Convert to Deal
                            </Button>
                        )}
                        <Button type="primary" htmlType="submit" >
                            Save
                        </Button>
                    </Flex>
                    {/* Editing splits the page in two: everything about the lead on
                        the left, the conversation with them on the right. Creating a
                        lead has no timeline yet, so the detail cards use the full
                        width instead. */}
                    <Row gutter={16}>
                        <Col xs={24} lg={id ? 12 : 24}>
                            <Row gutter={16}>
                                <Col xs={24} lg={id ? 24 : 12}>
                                    <Card title="Personal Information" bordered={false}>
                                        {id && (
                                            <Form.Item label="Lead Number" name="lead_number">
                                                <Input disabled style={{ fontWeight: 'bold', color: '#1890ff' }} />
                                            </Form.Item>
                                        )}
                                        <Form.Item label="First Name" name="first_name" rules={[{ required: true, message: 'Please input your first name!' }]}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Last Name" name="last_name">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Email" name="email">
                                            <Input type="email" />
                                        </Form.Item>
                                        <Form.Item label="Phone Number" name="phone_number">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            label="WhatsApp Number"
                                            name="wa_number"
                                            tooltip="Often the same as the phone number, but not always - this is the number WhatsApp templates go to."
                                        >
                                            <Input
                                                addonAfter={
                                                    <Tooltip title="Same as the phone number above">
                                                        <a onClick={useSameAsPhone} aria-label="Copy the phone number into this field">
                                                            <SwapRightOutlined />
                                                        </a>
                                                    </Tooltip>
                                                }
                                            />
                                        </Form.Item>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={id ? 24 : 12} style={{ paddingTop: 10 }}>
                                    <Card title="Contact Information" bordered={false}>
                                        <Form.Item
                                            label="Company Name"
                                            name="company_name"
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Job Title" name="job_title">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Team Size" name="team_size">
                                            <Select
                                                allowClear
                                                placeholder="Select team size"
                                                options={[
                                                    { value: 'SOLO', label: 'Just me' },
                                                    { value: '2_3', label: '2–3 people' },
                                                    { value: '4_5', label: '4–5 people' },
                                                    { value: '6_PLUS', label: '6 or more' },
                                                ]}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Address" name="address">
                                            <Input.TextArea rows={2} />
                                        </Form.Item>
                                        <Form.Item label="City" name="city">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="State" name="state">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Country" name="country">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Source" name="source">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Products" name="products">
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                allowClear
                                                placeholder="Select products (optional)"
                                                optionFilterProp="label"
                                                options={paymentProducts.map((item) => ({ value: item.id, label: item.name }))}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Tags" name="tags">
                                            <LeadTagSelect tags={leadTags} placeholder="Select tags (optional)" />
                                        </Form.Item>
                                        <Form.Item label="Communication Method" name="communication_method">
                                            <Input />
                                        </Form.Item>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={id ? 24 : 12} style={{ paddingTop: 10 }}>
                                    <Card title="Communication Log" bordered={false}>
                                        <Form.Item label="Status" name="status">
                                            <Select
                                                placeholder="Select Status"
                                                options={LEAD_STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))}
                                            />
                                        </Form.Item>
                                        {/* A converted lead has nothing left to follow up. */}
                                        <Form.Item noStyle dependencies={['status']}>
                                            {({ getFieldValue }) => getFieldValue('status') !== 'CONVERTED' && (
                                                <Form.Item label="Follow-up Date" name="follow_up_date">
                                                    <DatePicker showTime format="YYYY-MM-DD HH:mm" />
                                                </Form.Item>
                                            )}
                                        </Form.Item>
                                        <Form.Item label="Last Contacted At" name="last_contacted_at">
                                            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                                        </Form.Item>
                                        {id && (
                                            // Stamped by the server whenever the lead writes in on
                                            // any channel, so it is shown, not edited.
                                            <Form.Item label="Last Replied At">
                                                <Input
                                                    disabled
                                                    value={lead?.last_replied_at
                                                        ? dayjs(lead.last_replied_at).format('YYYY-MM-DD HH:mm:ss')
                                                        : 'No reply yet'}
                                                />
                                            </Form.Item>
                                        )}
                                        <Form.Item label="Notes" name="notes">
                                            <Input.TextArea />
                                        </Form.Item>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={id ? 24 : 12} style={{ paddingTop: 10 }}>
                                    <LeadRequirements requirement={lead?.requirement} />
                                </Col>
                                <Col xs={24} lg={id ? 24 : 12} style={{ paddingTop: 10 }}>
                                    <ExtraFieldsList savedKeys={savedExtraKeys} />
                                </Col>
                            </Row>
                        </Col>
                        {id && (
                            <Col xs={24} lg={12} style={{ paddingTop: 10 }}>
                                <div className="site-card-border-less-wrapper">
                                    <Card bordered={false}>
                                        <Tabs
                                            defaultActiveKey="1"
                                            items={tabElements}
                                        />
                                    </Card>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Form>
                <PdfPreviewModal
                    numPages={numPages}
                    onDocumentLoadSuccess={events.onDocumentLoadSuccess}
                    pdfPreviewData={pdfPreviewData}
                    previewPdfVisible={previewPdfVisible}
                    setPreviewPdfVisible={setPreviewPdfVisible}
                />
            </div>
            <Drawer
                title={`${currentActivityInstance ? 'Edit' : 'Add'} Lead Activity`}
                placement="right"
                width={isSendingActivity ? "50%" : "35%"}
                onClose={() => {
                    setCurrentActivityInstance(null)
                    setOpenActivityForm(false)
                    activityForm.resetFields();
                }}
                open={openActivityForm}
                style={{ zIndex: 10 }}
            >
                <LeadActivityForm
                    leadId={id}
                    lead={lead}
                    leadActivities={leadActivities}
                    setLeadActivities={setLeadActivities}
                    instance={currentActivityInstance}
                    form={activityForm}
                    onLeadStatusChange={(status) => {
                        // Keep the form in step too, or saving the lead would put the old status back.
                        setLead((prev) => ({ ...prev, status }));
                        form.setFieldValue('status', status);
                    }}
                    reload={() => {
                        setCurrentActivityInstance(null)
                        setOpenActivityForm(false)
                        activityForm.resetFields();
                        refreshActivities();
                        pollUntilDelivered();
                    }}
                />
            </Drawer>
            <Drawer
                title={`${currentTaskInstance ? 'Edit' : 'Add'} Task`}
                placement="right"
                width={"35%"}
                onClose={() => {
                    setCurrentTaskInstance(null)
                    setOpenTaskForm(false)
                    taskForm.resetFields();
                }}
                open={openTaskForm}
                style={{ zIndex: 10 }}
            >
                <LeadTaskForm
                    leadId={id}
                    tasks={tasks}
                    setTasks={setTasks}
                    instance={currentTaskInstance}
                    form={taskForm}
                    reload={() => {
                        setCurrentTaskInstance(null)
                        setOpenTaskForm(false)
                        taskForm.resetFields();
                    }}
                />
            </Drawer>
        </>
    );
}

export default LeadForm;